import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { JwtPayload, jwtDecode } from "jwt-decode";
import { User, OpenAPI, UserControllerService, BodyMeasurements, OnboardRequest } from "../.."
import { RootState } from "../../app/store";
import { LanguageCode } from "../../features/common/Language";
import { normalizeUserNutrition } from "../../common/nutritionTargets";
import { clearPersistedToken, persistToken } from "../../common/authStorage";

const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

interface UserState {
    loginStatus: 'NEW' | 'LOADING' | 'OK' | 'ERROR',
    onboardingLoading: boolean,
    onboardingError: string | null,
    language: LanguageCode.CS | LanguageCode.EN | LanguageCode.SK,
    user: User,
    newUser: User,
    jwt?: string,
    timezone: string
}

interface KonceptJwtPayload extends JwtPayload {
    userId: string;
    authorities: string[];
}

export function decodeJwtToUser(token?: string): Partial<User> {
    if (!token) {
        return {}
    }

    try {
        const payload: KonceptJwtPayload = jwtDecode(token)
        return {
            id: payload.userId,
            roles: payload.authorities
        }
    } catch (_err) {
        return {}
    }
}

const createEmptyUser = (timezone: string): User => ({
        sex: User.sex.MALE,
        physicalActivityLevel: 1.2,
        bodyMeasurements: [{
            type: BodyMeasurements.type.ACTUAL
        },
        {
            type: BodyMeasurements.type.PLAN
        }],
        timezone,
        mealtimeNutrients: {
            'BREAKFAST': { ENERGY: 0, PROTEIN: 0, FAT: 0, CARBOHYDRATE: 0 },
            'SNACK_AM': { ENERGY: 0, PROTEIN: 0, FAT: 0, CARBOHYDRATE: 0 },
            'LUNCH': { ENERGY: 0, PROTEIN: 0, FAT: 0, CARBOHYDRATE: 0 },
            'SNACK_PM': { ENERGY: 0, PROTEIN: 0, FAT: 0, CARBOHYDRATE: 0 },
            'DINNER': { ENERGY: 0, PROTEIN: 0, FAT: 0, CARBOHYDRATE: 0 }
        },
        languageCodes: []
    })

const createDraftUser = (timezone: string): User => ({
        sex: User.sex.MALE,
        physicalActivityLevel: 1.2,
        bodyMeasurements: [{
            type: BodyMeasurements.type.ACTUAL
        },
        {
            type: BodyMeasurements.type.PLAN
        }],
        timezone
    })

export const createInitialUserState = (timezone: string = timeZone): UserState => ({
    loginStatus: 'NEW',
    onboardingLoading: false,
    onboardingError: null,
    language: LanguageCode.CS,
    user: createEmptyUser(timezone),
    newUser: createDraftUser(timezone),
    timezone
})

const initialState: UserState = createInitialUserState()

const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        logout: () => {
            OpenAPI.TOKEN = undefined
            clearPersistedToken()
        },
        clearOnboardingDraft: (state) => {
            state.newUser = createDraftUser(state.timezone)
        },
        updateUserBodyMeasurements: (state, action: PayloadAction<BodyMeasurements>) => {
            if (!state.user.bodyMeasurements) {
                state.user.bodyMeasurements = []
            }
            const measurementType = action.payload.type ?? BodyMeasurements.type.ACTUAL
            const existing = state.user.bodyMeasurements.find((bm) => bm.type === measurementType)
            if (existing) {
                Object.assign(existing, action.payload)
            } else {
                state.user.bodyMeasurements.push({
                    ...action.payload,
                    type: measurementType
                })
            }
        },
        updateUserProfile: (state, action: PayloadAction<Partial<User>>) => {
            state.user = {
                ...state.user,
                ...action.payload
            }
        },
        updateNewUser: (state, action) => {
            switch (action.payload.name) {
                case 'username': state.newUser.username = action.payload.value
                    break
                case 'password': state.newUser.password = action.payload.value
                    break
                case 'sex': state.newUser.sex = action.payload.value
                    break
                case 'firstName': state.newUser.firstName = action.payload.value
                    break
                case 'lastName': state.newUser.lastName = action.payload.value
                    break
                case 'age': state.newUser.age = action.payload.value
                    break
                case 'physicalActivityLevel': state.newUser.physicalActivityLevel = action.payload.value
                    break
                case 'dietPreference': state.newUser.dietPreference = action.payload.value
                    break
                case 'email': state.newUser.email = action.payload.value
                    break
                case 'currentWeight': state.newUser.bodyMeasurements!![0].weight = action.payload.value
                    break
                case 'currentHeight': state.newUser.bodyMeasurements!![0].height = action.payload.value
                    break
                case 'measurementDate': state.newUser.bodyMeasurements!![0].date = action.payload.value
                    break
                case 'currentChest': state.newUser.bodyMeasurements!![0].chest = action.payload.value
                    break
                case 'currentWaist': state.newUser.bodyMeasurements!![0].waist = action.payload.value
                    break
                case 'currentHips': state.newUser.bodyMeasurements!![0].hips = action.payload.value
                    break
                case 'currentButtocks': state.newUser.bodyMeasurements!![0].buttocks = action.payload.value
                    break
                case 'currentThigh': state.newUser.bodyMeasurements!![0].thigh = action.payload.value
                    break
                case 'currentCalf': state.newUser.bodyMeasurements!![0].calf = action.payload.value
                    break
                case 'targetDate': state.newUser.bodyMeasurements!![1].targetDate = action.payload.value
                    break
                case 'plannedWeight': state.newUser.bodyMeasurements!![1].weight = action.payload.value
                    break
                case 'plannedChest': state.newUser.bodyMeasurements!![1].chest = action.payload.value
                    break
                case 'plannedWaist': state.newUser.bodyMeasurements!![1].waist = action.payload.value
                    break
                case 'plannedHips': state.newUser.bodyMeasurements!![1].hips = action.payload.value
                    break
                case 'plannedButtocks': state.newUser.bodyMeasurements!![1].buttocks = action.payload.value
                    break
                case 'plannedThigh': state.newUser.bodyMeasurements!![1].thigh = action.payload.value
                    break
                case 'plannedCalf': state.newUser.bodyMeasurements!![1].calf = action.payload.value
                    break
                case 'allergens': state.newUser.allergies = action.payload.value
                    break
                case 'preferredLanguages': state.newUser.languageCodes = action.payload.value
                    break
            }
        },
        updateTimezone: (state, action) => {
            state.timezone = action.payload
            state.user.timezone = action.payload
            state.newUser.timezone = action.payload
        },
        setLanguage: (state, action: PayloadAction<LanguageCode.CS | LanguageCode.EN | LanguageCode.SK>) => {
            state.language = action.payload
        }
    },
    extraReducers(builder) {
        builder
            .addCase(login.pending, (state, action) => {
                state.loginStatus = 'LOADING'
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loginStatus = 'OK'
                state.onboardingError = null
                state.jwt = action.payload
                OpenAPI.TOKEN = `${action.payload}`
                persistToken(action.payload)
                const user = decodeJwtToUser(action.payload)
                state.user = {
                    ...state.user,
                    ...user,
                }
            })
            .addCase(login.rejected, (state) => {
                state.loginStatus = 'ERROR'
                state.jwt = undefined
                OpenAPI.TOKEN = undefined
                clearPersistedToken()
            })
            .addCase(getUser.fulfilled, (state, action) => {
                state.user = normalizeUserNutrition(action.payload, state.user)
            })
            .addCase(initiateUser.fulfilled, (state, action) => {
                state.newUser = normalizeUserNutrition(action.payload.user, state.newUser)
            })
            .addCase(onboardUser.pending, (state) => {
                state.onboardingLoading = true
                state.onboardingError = null
            })
            .addCase(onboardUser.fulfilled, (state, action) => {
                state.onboardingLoading = false
                state.onboardingError = null
                state.user = normalizeUserNutrition(action.payload.user, state.user)
                state.jwt = action.payload.token
                state.loginStatus = 'OK'
                state.newUser = createDraftUser(state.timezone)
                OpenAPI.TOKEN = `${action.payload.token}`
                persistToken(action.payload.token)
            })
            .addCase(onboardUser.rejected, (state, action) => {
                state.onboardingLoading = false
                state.onboardingError = action.payload as string ?? action.error.message ?? 'Unknown error'
            })
    },
})

export const login = createAsyncThunk('user/login', async (user: User) => {
    const response = await UserControllerService.login(user)
    return response
})

export const getUser = createAsyncThunk('user/get', async () => {
    const response = await UserControllerService.getUser()
    return response
})

export const getUserById = createAsyncThunk('user/getById', async (id: string) => {
    const response = await UserControllerService.getUserById(id)
    return response
})

export const initiateUser = createAsyncThunk('user/initiate', async (user: User) => {
    const response = await UserControllerService.initiateUser(user)
    return response
})

export const onboardUser = createAsyncThunk('user/onboard', async (args: OnboardRequest, { rejectWithValue }) => {
    try {
        const response = await UserControllerService.onboard(args)
        return response
    } catch (error: any) {
        const actualErrorMessage = error.body?.message ?? error.message ?? 'unknown error'
        return rejectWithValue(actualErrorMessage)
    }
})

export const { logout, clearOnboardingDraft, updateNewUser, updateTimezone, setLanguage, updateUserBodyMeasurements, updateUserProfile } = userSlice.actions

export const selectJWT = (state: RootState) => state.user.jwt

export default userSlice.reducer
